import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { speakText } from "../utils/speech";
import confetti from "canvas-confetti";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import HomeIcon from "@mui/icons-material/Home";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimerIcon from "@mui/icons-material/Timer";
import StarsIcon from "@mui/icons-material/Stars";
import ReplayIcon from "@mui/icons-material/Replay";
import "../styles/LetterArrange.css";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const fmt = (ms) => (ms / 1000).toFixed(1) + "s";

function pbKey(practiceType, word) {
  return practiceType === "chinese"
    ? `la_pb_zh_${word.chinese}`
    : `la_pb_en_${word.word.toLowerCase()}`;
}

export default function LetterArrangeGame({ words, practiceType, kidName, onExit }) {
  const isChinese = practiceType === "chinese";

  const [wordIndex, setWordIndex]     = useState(0);
  const [slots, setSlots]             = useState([]);
  const [pool, setPool]               = useState([]);
  const [dragState, setDragState]     = useState(null);
  const [result, setResult]           = useState(null);
  const [elapsed, setElapsed]         = useState(0);
  const [finalTime, setFinalTime]     = useState(null);
  const [bestTime, setBestTime]       = useState(null);
  const [isNewBest, setIsNewBest]     = useState(false);
  const [score, setScore]             = useState(0);
  const [done, setDone]               = useState(false);
  const [sessionTimes, setSessionTimes] = useState([]);

  const timerRef        = useRef(null);
  const startTimeRef    = useRef(null);
  const timerStartedRef = useRef(false);
  const slotsRef        = useRef([]);
  const poolRef         = useRef([]);
  const resultRef       = useRef(null);
  const wordIndexRef    = useRef(0);

  slotsRef.current    = slots;
  poolRef.current     = pool;
  resultRef.current   = result;
  wordIndexRef.current = wordIndex;

  const currentWord = words[wordIndex];

  const getChars = useCallback((word) => {
    const str = isChinese ? word.chinese : word.word.toUpperCase();
    return str.split("").map((c, i) => ({ id: i, letter: c }));
  }, [isChinese]);

  const getLen = useCallback((word) => {
    return isChinese ? word.chinese.length : word.word.length;
  }, [isChinese]);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    startTimeRef.current = performance.now();
    timerRef.current = setInterval(() => {
      setElapsed(performance.now() - startTimeRef.current);
    }, 100);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    return timerStartedRef.current ? performance.now() - startTimeRef.current : 0;
  }, []);

  const markCorrect = useCallback((t, word) => {
    const key = pbKey(practiceType, word);
    const saved = parseInt(localStorage.getItem(key));
    const prev = isNaN(saved) ? null : saved;
    const newBest = !prev || t < prev;
    if (newBest) localStorage.setItem(key, Math.round(t));
    setFinalTime(t);
    setIsNewBest(newBest);
    setBestTime(newBest ? Math.round(t) : prev);
    setResult("correct");
    setScore(s => s + 1);
    setSessionTimes(prev => [...prev, {
      word: isChinese ? word.chinese : word.word,
      time: t,
      isNewBest: newBest,
    }]);
    confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, ticks: 120 });
  }, [practiceType, isChinese]);

  const initWord = useCallback(() => {
    if (!currentWord) return;
    clearInterval(timerRef.current);

    const len = getLen(currentWord);
    if (len === 0) return;

    const key = pbKey(practiceType, currentWord);
    const saved = parseInt(localStorage.getItem(key));
    setBestTime(isNaN(saved) ? null : saved);
    setResult(null);
    setFinalTime(null);
    setIsNewBest(false);
    setElapsed(0);
    setDragState(null);

    const chars = getChars(currentWord);
    const target = isChinese ? currentWord.chinese : currentWord.word.toUpperCase();
    let shuffled = shuffle(chars);
    let tries = 0;
    while (len > 1 && shuffled.map(t => t.letter).join("") === target && tries++ < 20) {
      shuffled = shuffle(chars);
    }
    setPool(shuffled);
    setSlots(Array(len).fill(null));

    timerStartedRef.current = false;

    speakText(
      isChinese ? currentWord.chinese : currentWord.word,
      isChinese ? "zh" : "en",
      0.9,
      () => { timerStartedRef.current = true; startTimer(); }
    );
  }, [currentWord, getChars, getLen, isChinese, practiceType, startTimer]);

  useEffect(() => {
    initWord();
    return () => clearInterval(timerRef.current);
  }, [wordIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-check when all slots filled
  useEffect(() => {
    if (!currentWord || resultRef.current) return;
    const len = getLen(currentWord);
    if (slots.length !== len || pool.length > 0 || !slots.every(s => s !== null)) return;

    const answer = slots.map(s => s.letter).join("");
    const target = isChinese ? currentWord.chinese : currentWord.word.toUpperCase();
    if (answer === target) {
      const t = stopTimer();
      markCorrect(t, currentWord);
      setTimeout(() => {
        const idx = wordIndexRef.current;
        if (idx + 1 >= words.length) setDone(true);
        else setWordIndex(idx + 1);
      }, 1500);
    } else {
      setResult("wrong");
      const returned = [...slots].filter(Boolean);
      setTimeout(() => {
        setSlots(Array(len).fill(null));
        setPool(returned);
        setResult(null);
      }, 800);
    }
  }, [slots, pool]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pointer drag handlers
  useEffect(() => {
    if (!dragState) return;

    const onMove = (e) => {
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      if (x == null) return;
      setDragState(d => {
        if (!d) return null;
        const moved = Math.hypot(x - d.startX, y - d.startY) > 6;
        return { ...d, ghostX: x, ghostY: y, isDragging: d.isDragging || moved };
      });
    };

    const onUp = (e) => {
      if (resultRef.current) { setDragState(null); return; }
      const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
      const y = e.clientY ?? e.changedTouches?.[0]?.clientY;
      const { tile, isDragging, fromSlot } = dragState;

      if (!isDragging) {
        // Tap (no movement)
        if (fromSlot) {
          // Tapped a placed tile → return to pool
          setPool(prev => [...prev, tile]);
        } else {
          // Tapped a pool tile → place in first available slot
          const firstEmpty = slotsRef.current.findIndex(s => s === null);
          if (firstEmpty !== -1) {
            const newSlots = [...slotsRef.current];
            newSlots[firstEmpty] = tile;
            setSlots(newSlots);
            setPool(poolRef.current.filter(t => t.id !== tile.id));
          }
        }
      } else if (x != null) {
        // Drag — find drop target
        const el = document.elementFromPoint(x, y);
        const slotEl = el?.closest("[data-slot-index]");
        if (slotEl) {
          // Dropped on a slot
          const idx = parseInt(slotEl.dataset.slotIndex, 10);
          const newSlots = [...slotsRef.current];
          const displaced = newSlots[idx];
          newSlots[idx] = tile;
          const newPool = poolRef.current
            .filter(t => t.id !== tile.id)
            .concat(displaced ? [displaced] : []);
          setSlots(newSlots);
          setPool(newPool);
        } else if (fromSlot) {
          // Slot tile dropped outside any slot → return to pool
          setPool(prev => [...prev, tile]);
        }
        // Pool tile dropped outside slot → stays in pool (never removed)
      }
      setDragState(null);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragState]);

  const startDrag = (e, tile, fromSlot = false) => {
    e.preventDefault();
    setDragState({ tile, ghostX: e.clientX, ghostY: e.clientY, startX: e.clientX, startY: e.clientY, isDragging: false, fromSlot });
  };

  const handleTilePointerDown = (e, tile) => {
    if (result) return;
    startDrag(e, tile, false);
  };

  const handleSlotPointerDown = (e, tile, slotIdx) => {
    if (result) return;
    setSlots(prev => { const n = [...prev]; n[slotIdx] = null; return n; });
    startDrag(e, tile, true);
  };

  const handleRestart = () => {
    setWordIndex(0);
    setScore(0);
    setDone(false);
    setSessionTimes([]);
  };

  // Summary screen
  if (done) {
    const timed = sessionTimes.filter(s => s.time > 0);
    const avg = timed.length ? timed.reduce((s, r) => s + r.time, 0) / timed.length : 0;
    return (
      <div className="la-summary">
        <div className="la-summary-emoji">🏆</div>
        <h2 className="la-summary-title">Well done{kidName ? `, ${kidName}` : ""}!</h2>
        <div className="la-summary-score">{score} / {words.length}</div>
        {avg > 0 && (
          <div className="la-summary-avg">
            <TimerIcon style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }} />
            Avg {fmt(avg)}
          </div>
        )}
        <div className="la-session-times">
          {sessionTimes.map((s, i) => (
            <div key={i} className="la-session-row">
              <span className="la-session-word">{s.word}</span>
              <span className="la-session-time">{s.time === 0 ? "—" : fmt(s.time)}</span>
              {s.isNewBest && s.time > 0 && <span className="la-session-pb">⭐ Best!</span>}
            </div>
          ))}
        </div>
        <div className="la-summary-actions">
          <button className="la-btn la-btn-ghost" onClick={handleRestart}>
            <ReplayIcon style={{ verticalAlign: "middle", marginRight: 4 }} /> Play Again
          </button>
          {onExit && (
            <button className="la-btn la-btn-primary" onClick={onExit}>
              <HomeIcon style={{ verticalAlign: "middle", marginRight: 4 }} /> Menu
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!currentWord) return null;
  const wordLen = getLen(currentWord);

  return (
    <div className="la-wrap">
      {/* Dragging ghost — only shown once movement threshold exceeded */}
      {dragState?.isDragging && createPortal(
        <div
          className={`la-tile la-tile-ghost${isChinese ? " la-tile-chinese" : ""}`}
          style={{
            position: "fixed",
            left: dragState.ghostX,
            top: dragState.ghostY,
            transform: "translate(-50%, -50%) rotate(4deg) scale(1.12)",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          {dragState.tile.letter}
        </div>,
        document.body
      )}

      {/* Progress bar */}
      <div className="la-progress-bar">
        <div className="la-progress-fill" style={{ width: `${(wordIndex / words.length) * 100}%` }} />
      </div>

      <div className="la-status">
        <span className="la-word-count">{wordIndex + 1} / {words.length}</span>
        <span className="la-score-label">Score: {score}</span>
      </div>

      {/* Live timer + personal best */}
      <div className="la-timer-row">
        <div className={`la-timer${result === "correct" ? " la-timer-stopped" : ""}`}>
          <TimerIcon className="la-timer-icon" />
          {result === "correct" && finalTime !== null
            ? fmt(finalTime)
            : timerStartedRef.current ? fmt(elapsed) : "– –"}
        </div>
        {bestTime !== null && (
          <div className="la-best-time">
            <EmojiEventsIcon className="la-best-icon" />
            Best: {fmt(bestTime)}
          </div>
        )}
      </div>

      {/* Clue */}
      <div className="la-clue">
        <button
          className="la-speak-btn"
          onClick={() => speakText(
            isChinese ? currentWord.chinese : currentWord.word,
            isChinese ? "zh" : "en",
            0.9
          )}
        >
          <VolumeUpIcon /> {isChinese ? "听一听" : "Hear the word"}
        </button>
        {isChinese ? (
          <>
            {currentWord.pinyin  && <div className="la-meaning-en">{currentWord.pinyin}</div>}
            {currentWord.english && <div className="la-meaning-zh">{currentWord.english}</div>}
          </>
        ) : (
          <>
            {currentWord.meaningEn && <div className="la-meaning-en">"{currentWord.meaningEn}"</div>}
            {currentWord.meaningZh && <div className="la-meaning-zh">{currentWord.meaningZh}</div>}
          </>
        )}
      </div>

      {/* Answer slots */}
      <div className={`la-slots${isChinese ? " la-slots-chinese" : ""}`}>
        {Array.from({ length: wordLen }).map((_, i) => (
          <div
            key={i}
            data-slot-index={i}
            className={[
              "la-slot",
              isChinese ? "la-slot-chinese" : "",
              slots[i] ? `filled${result === "correct" ? " correct" : result === "wrong" ? " wrong" : ""}` : "",
              dragState?.isDragging && !slots[i] ? "droppable" : "",
            ].filter(Boolean).join(" ")}
            onPointerDown={slots[i] && !result ? e => handleSlotPointerDown(e, slots[i], i) : undefined}
            style={slots[i] && !result ? { touchAction: "none", cursor: "grab" } : undefined}
          >
            {slots[i]?.letter || ""}
          </div>
        ))}
      </div>

      {/* Result banner */}
      {result === "correct" && finalTime !== null && (
        <div className="la-result-correct">
          {isNewBest
            ? <><StarsIcon /> New Best! {fmt(finalTime)}</>
            : <><span>✓</span> {fmt(finalTime)}{bestTime ? ` · Best: ${fmt(bestTime)}` : ""}</>
          }
        </div>
      )}

      {/* Tile pool */}
      {!result && (
        <div className="la-tiles">
          {pool.map(tile => (
            <div
              key={tile.id}
              className={[
                "la-tile",
                isChinese ? "la-tile-chinese" : "",
                dragState?.tile.id === tile.id ? "la-tile-dragging" : "",
              ].filter(Boolean).join(" ")}
              onPointerDown={e => handleTilePointerDown(e, tile)}
              style={{ touchAction: "none", cursor: "grab" }}
            >
              {tile.letter}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
